import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ApplicationStatusRetrievalService } from 'src/app/shared/services/application-status-retrieval.service';
import { SearchResponse } from '../interfaces/search-response.interface';
import { DOCUMENT_UPLOAD_FILTER, ENROLLMENT_TYPE, ENROLLMENT_TYPE_FILTERABLE, SearchParams, SORT_ORDER, SORT_ORDER_FILTERABLE } from '../interfaces/search-params.interface';
import { ApplicationRecord } from '../interfaces/application-record.interface';
import { LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure, LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { environment } from 'src/environments/environment';
import { BooleanInput, GlobalShadowStyleDirective } from '@ptl/global-components';
import { SAVE_QUOTE_CARD_TYPE, SavedQuotesInputs } from './interfaces/saved-quotes.input.interface';
import { SaveQuoteCardDeleteQuoteEvent } from './interfaces/saved-quotes.input.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { Subject, takeUntil } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ModalFieldConfigs } from './interfaces/save-quote-email-referral.interface';

@Component({
    selector: 'app-saved-quotes',
    templateUrl: './saved-quotes.component.html',
    styleUrls: ['./saved-quotes.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class SavedQuotesComponent extends GlobalShadowStyleDirective implements OnInit, OnDestroy, AfterViewInit {

  @Input() planNameLabel: string;
  @Input() planMonthlyPremiumLabel: string;
  @Input() planDeductibleLabel: string;
  @Input() creationDateLabel: string;
  @Input() deleteQuoteButtonLabel: string;
  @Input() continueApplicationButtonLabel: string;
  @BooleanInput() @Input() showExpiryNotice: boolean;
  @BooleanInput() @Input() showSearch: boolean;

  @Input() oepEndDate: Date | string;
  @Input() lastDayJanuaryEffectiveDate: Date | string;
  
  @Input() quoteType: SavedQuotesInputs["TQuoteType"];

  @Input() disableEmailReferral: boolean; // Controls whether we see the email fields in the card
  @Input() emailReferralInputType: string;
  @Input() disableReferralTooltip: string; //For modal only
  @Input() fieldsToDisplay: string;
  @Input() emailFieldLabel: string;
  @Input() emailFieldPlaceholder: string;
  @Input() sendEmailButtonLabel: string;
  @Input() emailSuccessStatusMessage: string;
  @Input() additionalModalFields: ModalFieldConfigs[];

  useEmailModal: boolean; 
  isReadyToDisplay: boolean = false;

  applicationRecords: ApplicationRecord[] = [];
  totalRecords: number; // this.applicationRecords are paginated on server-side, totalRecords is not the filtered data
  applicationRecordsPerPage = 10;

  agencyId: string; 
  marketType: LINE_OF_BUSINESS_ENUM = LINE_OF_BUSINESS_ENUM.INDIVIDUAL;
  enrollmentType: ENROLLMENT_TYPE = ENROLLMENT_TYPE.ALL_TYPES;
  appId: string;

  sortDirection: SORT_ORDER = SORT_ORDER.DESCENDING;

  @ViewChild("SavedQuotesPaginator") paginator: MatPaginator;
  dataSource: MatTableDataSource<ApplicationRecord>;
  destroy$: Subject<void> = new Subject(); // For use in emitting onDestroy

  paginationForm: UntypedFormGroup;


  constructor(
    private appStatusRetrieveService: ApplicationStatusRetrievalService,
    private reducerUtilityService: ReducerUtilityService,
    private matSnackBar: MatSnackBar,
    private fb: UntypedFormBuilder
  ) {
    super();

    this.paginationForm = this.fb.group({
      paginationControl: this.fb.control(10, {nonNullable: true})
    });

  }

  trackByAppId(index: number, plan: ApplicationRecord): string { //trackBy card specific index ie application id 
    return plan.applicationTrackingId;
  }

  async ngOnInit() {
    const lob = this.reducerUtilityService.getApplicationStates().Lob.lob
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    this.agencyId = loginInfo.userInfo.agencyInfo.selectedAgencies[lob].agencyId;
    this.useEmailModal = this.emailReferralInputType === 'POPUP_MODAL' ? true : false;
    await this.fetchApplications();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(async () => {
      await this.fetchApplications();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Back to Page 0 */
  resetPaginationState() {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  async fetchApplications(): Promise<SearchResponse> {
    this.isReadyToDisplay = false;
    
    const basePayload: SearchParams = {
      productType: this.marketType,
      pageNumber: this.paginator?.pageIndex || 0,
      applicationTrackingNumber: this.appId ? this.appId : undefined,
      countPerPage: this.paginator?.pageSize || this.applicationRecordsPerPage,
      combinedStatus: "IN PROGRESS" //get ALL applications
    }

    if (environment.initData.brand.ASPID === 'MN'){
      basePayload.agencyId = this.agencyId;
    }

    let response: SearchResponse = null;
    try {
      const searchParams = this.getServerSideFilters(basePayload);
      response = await this.getApplicationsBySearchParams(searchParams);
      
      this.applicationRecords = response.searchedApplications;

      /** Update Paginator with server-side total */
      if (this.paginator) {
        this.paginator.length = response.totalApplicationCount;
      }
      
      this.isReadyToDisplay = true;
    } catch (e) {
      console.log(e);
    }

    return response;
  }

  /** Enhance the searchParams payload with additional filtering on backend side ( to support pagination logic ) */
  getServerSideFilters(searchParams: SearchParams): SearchParams {
    
    searchParams.filter = [];

    /** DOCUMENT UPLOAD */
    if (this.quoteType === SAVE_QUOTE_CARD_TYPE.DOCUMENT_UPLOAD_REQUEST) {
      const documentUploadFilter: DOCUMENT_UPLOAD_FILTER = {
        key: "DOCUMENT_UPLOAD",
        value: "TRUE"
      }
      searchParams.filter.push(documentUploadFilter)
    }

    /** SORT DIRECTION */
    if (this.sortDirection) {
      const sortOrderFilter: SORT_ORDER_FILTERABLE = {
        key: "SORT_ORDER",
        value: this.sortDirection
      }
      searchParams.filter.push(sortOrderFilter)
    }

    /** ENROLLMENT TYPE */
    if (this.enrollmentType && this.enrollmentType !== ENROLLMENT_TYPE.ALL_TYPES) {
      const enrollmentTypeFilter: ENROLLMENT_TYPE_FILTERABLE = {
        key: "ENROLLMENT_TYPE",
        value: this.enrollmentType
      }
      searchParams.filter.push(enrollmentTypeFilter)
    }

    return searchParams;

  }
  async getApplicationsBySearchParams(searchParams: SearchParams): Promise<SearchResponse> {
    const response: SearchResponse = await this.appStatusRetrieveService.getApplications(searchParams);
    return response;
  }


  /** Sort ( server-side ) */
  async onSortDirectionChange(sortDirection: SORT_ORDER) {
    this.sortDirection = sortDirection;
    await this.fetchApplications();
  }

  /** Fetch Data */
  async newFetchResetPage() {
    this.resetPaginationState();
    await this.fetchApplications();
  }
  // Application ID text search
  async searchPlans() {
    await this.newFetchResetPage();
  }
  // Application ID reset
  async clearSearch() {
    this.appId = '';
    await this.newFetchResetPage();
  }
  // Enrollment Type filter
  async filterApplications() {
    await this.newFetchResetPage();
  }

  /** Emit Alert; Refetch data from page 0 */
  onDeleteQuote(eventData: SaveQuoteCardDeleteQuoteEvent): void {
    this.applicationRecords = this.applicationRecords.filter(record => record.applicationTrackingId !== eventData.id);

    let communication = '';
    if (eventData.name) {
      communication += `Name: ${eventData.name}`;
      if (eventData.id) {
        communication += ` | ID: ${eventData.id}`;
      }
    } else if (eventData.id) {
      communication += `ID: ${eventData.id}`;
    }

    if (eventData.showAlert) {
      this.matSnackBar.open(`QUOTE DELETED! ${communication}`, 'OK', { duration: 3000, panelClass: 'positive-panel-class' });
    }
    
    this.newFetchResetPage();
  }
}
